import { CSSObject, Divider, styled, Theme } from '@mui/material';
import MuiDrawer from '@mui/material/Drawer';
import { RiMenuFoldLine, RiMenuUnfoldLine, RiSettings2Line } from '@remixicon/react';
import { useRouter } from '@tanstack/react-router';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useIsCustomer, useIsMember } from 'src/auth';
import { buildAppHref } from 'src/lib/routing';
import { appLinks } from 'src/routing/appLinks';
import { useSidebar } from '../../providers/SidebarContextProvider';
import CustomerPortalNavigation from '../customer-portal/CustomerPortalNavigation';
import MemberNavigation from './navigation/MemberNavigation';
import RouteFavorites from './navigation/RouteFavorites';
import SidebarLink from './navigation/SidebarLink';
import { GLOBAL_APP_BAR_HEIGHT } from './SiteHeader';

export const GLOBAL_SIDE_BAR_WIDTH = 256;

const openedMixin = (theme: Theme): CSSObject => ({
  width: GLOBAL_SIDE_BAR_WIDTH,
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: 'hidden',
});

const closedMixin = (theme: Theme): CSSObject => ({
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: 'hidden',
  width: `calc(${theme.spacing(7)} + 1px)`,
  [theme.breakpoints.up('sm')]: { width: `calc(${theme.spacing(8)} + 1px)` },
});

const Drawer = styled(MuiDrawer, { shouldForwardProp: (prop) => prop !== 'open' })(({ theme, open }) => ({
  width: GLOBAL_SIDE_BAR_WIDTH,
  flexShrink: 0,
  whiteSpace: 'nowrap',
  boxSizing: 'border-box',
  '& .MuiDrawer-paper': { marginTop: GLOBAL_APP_BAR_HEIGHT },
  ...(open && { ...openedMixin(theme), '& .MuiDrawer-paper': openedMixin(theme) }),
  ...(!open && { ...closedMixin(theme), '& .MuiDrawer-paper': closedMixin(theme) }),
}));

export default function SideNavigation() {
  const router = useRouter();
  const { sidebarOpen, toggleOpen } = useSidebar();
  const { permissionsCanViewSystemSettings } = useFlags();
  const isMember = useIsMember();
  const isCustomer = useIsCustomer();

  return (
    <Drawer
      variant="permanent"
      open={sidebarOpen}
      slotProps={{
        paper: {
          className: 'border-r border-neutral-5 pb-2',
          sx: { mt: `${GLOBAL_APP_BAR_HEIGHT}px`, maxHeight: `calc(100vh - ${GLOBAL_APP_BAR_HEIGHT}px)` },
        },
      }}
    >
      <div className={`col p-2 h-full ${sidebarOpen ? 'items-start' : 'items-center'}`}>
        {isMember && <MemberNavigation />}
        {isCustomer && <CustomerPortalNavigation />}
        <Divider sx={{ my: 1, width: '100%' }} />
        <SidebarLink
          text={sidebarOpen ? 'Collapse' : 'Expand'}
          icon={() => (sidebarOpen ? <RiMenuFoldLine /> : <RiMenuUnfoldLine />)}
          onClick={toggleOpen}
        />

        {/* Spacer to give the favorites some room, but don't push them to the bottom */}
        <div className="grow" />
        <RouteFavorites />
        {permissionsCanViewSystemSettings && isMember && (
          <>
            <Divider sx={{ my: 1, width: '100%' }} />
            <SidebarLink
              text="Settings"
              icon={() => <RiSettings2Line />}
              href={buildAppHref(router, appLinks.settings())}
              testId="nav-settings"
            />
          </>
        )}
      </div>
    </Drawer>
  );
}
